// Component dropdown có search - dùng react-select
import { useMemo } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useLingui } from '@lingui/react/macro';
import { tOptions } from '../../i18n/runtime.js';

const customStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    borderRadius: '0.75rem',
    minHeight: '42px',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#3b82f6' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '0.875rem',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
};

export default function SearchableSelect({
  options: optionsRaw = [],
  value,
  onChange,
  // Ba chữ mặc định dưới đây DỊCH LÚC VẼ (macro t trong thân hàm): viết thẳng chuỗi tiếng Việt vào
  // giá trị mặc định của tham số thì mọi ô chọn không truyền chữ riêng đều lòi tiếng Việt trên
  // màn tiếng Anh («Không tìm thấy»). `placeholder=""` vẫn được tôn trọng (?? chứ không ||).
  placeholder,
  isClearable = true,
  isDisabled = false,
  isMulti = false,
  noOptionsMessage,
  formatOptionLabel, // optional: custom render option (vd gắn badge) — search vẫn theo option.label
  // Hai cửa để chỗ gọi TỰ lọc/xếp hạng thay vì dùng bộ lọc chuỗi thô của react-select
  // (nó chỉ so `label`+`value`, không bỏ dấu, không xếp hạng). Bỏ trống = giữ y hành vi cũ.
  // Dùng cặp: onInputChange để biết đang gõ gì, filterOption={() => true} để nhận nguyên
  // danh sách mình đã lọc sẵn. Xem components/crm/ProductPicker.jsx.
  onInputChange,
  filterOption,
  // Bề rộng TỐI THIỂU (px) của danh sách xổ xuống. react-select bó menu bằng đúng bề rộng
  // ô; ô nằm trong cột hẹp (cột "Sản phẩm" của báo giá ≈ 145px) thì mỗi dòng vỡ 3-4 hàng,
  // đọc không nổi. Menu bung sang phải và tự co lại khi màn hình hẹp hơn.
  menuMinWidth,
  // Cho phép gõ giá trị chưa có trong danh mục → tạo mới ngay tại chỗ.
  // Dùng cho danh mục chức vụ: HR đang nhập hợp đồng thì gõ luôn chức danh mới,
  // không phải rời màn hình sang trang danh mục rồi quay lại.
  isCreatable = false,
  onCreateOption,               // (inputValue) => void | Promise — host tự gọi API tạo
  formatCreateLabel,
}) {
  const { t } = useLingui();

  // 🛡️ Hàng rào descriptor: bảng nhãn hay được ném nguyên cụm vào đây qua prop
  // (`options={KIND_OPTIONS}`). Dịch NGAY Ở ĐÂY để chỗ gọi không phải nhớ — xem tOptions.
  const options = useMemo(() => tOptions(optionsRaw), [optionsRaw]);

  const styles = useMemo(() => {
    if (!menuMinWidth) return customStyles;
    const w = typeof menuMinWidth === 'number' ? `min(${menuMinWidth}px, calc(100vw - 24px))` : menuMinWidth;
    return { ...customStyles, menu: (base) => ({ ...customStyles.menu(base), minWidth: w }) };
  }, [menuMinWidth]);

  // Hỗ trợ cả flat options lẫn grouped options ([{ label, options: [...] }]).
  // Flatten để lookup selected value đúng khi dùng grouped (group không có .value).
  const flatOptions = options.flatMap((o) => (Array.isArray(o.options) ? o.options : [o]));
  const selectedOption = isMulti
    ? flatOptions.filter((o) => Array.isArray(value) && value.includes(o.value))
    : flatOptions.find((o) => o.value === value) || null;

  // Creatable dùng cùng styles/hành vi, chỉ thêm đường tạo mới → tránh fork 2 component.
  const Cmp = isCreatable ? CreatableSelect : Select;

  return (
    <Cmp
      styles={styles}
      options={options}
      value={selectedOption}
      onChange={(opt) => {
        if (isMulti) {
          onChange(Array.isArray(opt) ? opt.map(o => o.value) : []);
        } else {
          onChange(opt ? opt.value : '');
        }
      }}
      placeholder={placeholder ?? t`Tìm kiếm...`}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={true}
      isMulti={isMulti}
      formatOptionLabel={formatOptionLabel}
      onInputChange={onInputChange}
      {...(filterOption ? { filterOption } : {})}
      noOptionsMessage={noOptionsMessage || (() => t`Không tìm thấy`)}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      menuPosition="fixed"
      {...(isCreatable ? {
        onCreateOption,
        formatCreateLabel: formatCreateLabel || ((v) => t`Thêm mới "${v}"`),
        // Không cho tạo bản chỉ khác khoảng trắng/hoa-thường với mục đã có — đó chính là
        // cách danh mục bị đẻ biến thể. DB cũng có UNIQUE lower(name) chặn lần cuối.
        isValidNewOption: (input) => {
          const v = String(input || '').trim().toLowerCase();
          if (!v) return false;
          return !flatOptions.some((o) => String(o.label || '').trim().toLowerCase() === v);
        },
      } : {})}
    />
  );
}
